/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { SelectableGuide } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Avatar,
  colors,
  InlineIcon,
  numericPercentages,
  numericValues,
  PauseIcon,
  PlayIcon,
  ProgressCircle,
  radius,
  spacing,
  Text,
  tints,
  useLocalRef,
  useThemeValues,
  VideoIcon,
} from "@packages/ui";
import type { ReactEventHandler } from "react";
import { forwardRef, useState } from "react";

const avatarStyle: Interpolation = {
  gridArea: "avatar",
  position: "relative",
};
const avatar1Style: Interpolation = {
  left: 0,
  position: "absolute",
  top: 0,
};
const avatar2Style: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  borderColor: theme.semanticColors.onOverlayBackground,
  borderRadius: radius.full,
  borderStyle: "solid",
  borderWidth: 4,
  bottom: 0,
  boxSizing: "content-box",
  display: "flex",
  justifyContent: "center",
  position: "absolute",
  right: 0,
});
const avatar2HoveredStyle: Interpolation<ThemeType> = (theme) => ({
  borderColor: theme.semanticColors.onOverlayLow,
});
const avatarsStyle: Interpolation = {
  height: 64,
  position: "relative",
  width: 64,
};
const containerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  rowGap: spacing.s,
  width: numericPercentages[100],
};
const headerStyle: Interpolation = {
  columnGap: spacing.ms,
  display: "grid",
  gridTemplateAreas: '"avatar title" "avatar tagline"',
  gridTemplateColumns: "min-content 1fr",
  gridTemplateRows: "1fr 1fr",
  rowGap: spacing.xxs,
};
const headerWithVideoStyle: Interpolation = {
  gridTemplateAreas: '"avatar title" "avatar tagline" "avatar video"',
  gridTemplateRows: "1fr 1fr 1fr",
  rowGap: spacing.t,
};
const nameStyle: Interpolation = {
  gridArea: "title",
  margin: numericValues[0],
  placeSelf: "end start",
};
const sampleStyle: Interpolation<ThemeType> = (theme) => ({
  background: theme.semanticColors.onOverlayLow,
  border: numericValues[0],
  borderRadius: radius.full,
  bottom: numericValues[0],
  cursor: "pointer",
  lineHeight: numericValues[0],
  outline: "none",
  padding: numericValues[0],
  position: "absolute",
  right: numericValues[0],
});
const taglineStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.onOverlayMedHigh,
  gridArea: "tagline",
  placeSelf: "start start",
});
const taglineWithVideoStyle: Interpolation = {
  placeSelf: "center start",
};
const videoIndicatorStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  color: theme.semanticColors.onOverlayMedium,
  columnGap: spacing.xs,
  display: "flex",
  gridArea: "video",
  justifyContent: "flex-start",
  placeSelf: "start start",
});

export type GuideInfoProps = {
  guide?: SelectableGuide;
  includeSample?: boolean;
  isHovered?: boolean;
  pauseOthers?: () => void;
};

export const GuideInfo = forwardRef<HTMLAudioElement, GuideInfoProps>(
  ({ guide, includeSample = false, pauseOthers, isHovered }, ref) => {
    const sampleRef = useLocalRef<HTMLAudioElement>(ref);
    const [playing, setPlaying] = useState<boolean>(false);
    const [progress, setProgress] = useState<number>(0);
    const [started, setStarted] = useState<boolean>(false);
    const t = useTranslations();
    const theme = useThemeValues();

    const isVideo = guide?.tracks?.some(
      (track) => track.media_type === "video",
    );

    if (!guide) return null;

    const playSample = () => {
      if (pauseOthers) pauseOthers();
      if (sampleRef.current?.paused) {
        sampleRef.current?.play();
      } else {
        sampleRef.current?.pause();
      }
    };

    const updateProgress: ReactEventHandler<HTMLAudioElement> = (e) => {
      requestAnimationFrame(() => {
        setProgress(
          (e.target as HTMLAudioElement).currentTime /
            (e.target as HTMLAudioElement).duration,
        );
      });
    };

    const handlePlay = () => {
      setPlaying(true);
      setStarted(true);
    };

    const sample =
      includeSample && guide.sample_audio_url ? (
        <>
          <button css={sampleStyle} onClick={playSample}>
            <svg
              stroke={"none"}
              fill={"none"}
              height={24}
              viewBox={"0 0 24 24"}
              width={24}
              xmlns="http://www.w3.org/2000/svg"
            >
              {!started ? (
                <circle cx={12} cy={12} r={10} fill={colors.white} />
              ) : (
                <ProgressCircle
                  circleRadius={10}
                  halfSvgWidth={12}
                  progress={progress}
                  stroke={colors.white}
                  backgroundStroke={tints.tint20}
                />
              )}
              {!playing ? (
                <PlayIcon.Medium
                  size={12}
                  fill={
                    !started ? theme.semanticColors.onOverlayLow : colors.white
                  }
                  x={6}
                  y={6}
                />
              ) : (
                <PauseIcon.Medium size={12} fill={colors.white} x={6} y={6} />
              )}
            </svg>
          </button>
          <audio
            ref={sampleRef}
            src={guide.sample_audio_url}
            onTimeUpdate={updateProgress}
            onEnded={() => setStarted(false)}
            onPlaying={handlePlay}
            onPause={() => setPlaying(false)}
          />
        </>
      ) : null;

    return (
      <div css={containerStyle}>
        <div css={[headerStyle, isVideo && headerWithVideoStyle]}>
          <div css={avatarStyle}>
            {guide.guide_images?.length > 1 ? (
              <div css={avatarsStyle}>
                <Avatar
                  name={guide.name}
                  imgSrc={guide.guide_images[0].fallback}
                  size={40}
                  css={avatar1Style}
                />
                <Avatar
                  name={guide.name}
                  imgSrc={guide.guide_images[1].fallback}
                  size={40}
                  css={[avatar2Style, isHovered && avatar2HoveredStyle]}
                />
              </div>
            ) : (
              <Avatar
                name={guide.name}
                imgSrc={guide.guide_images[0].fallback}
                size={64}
              />
            )}
            {sample}
          </div>
          <Text as={"h2"} type={"title"} size={"m"} css={nameStyle}>
            {guide.name}
          </Text>
          <Text
            type={"detail"}
            size={"l"}
            css={[taglineStyle, isVideo && taglineWithVideoStyle]}
          >
            {guide.tagline}
          </Text>
          {isVideo ? (
            <Text type={"detail"} size={"l"} css={videoIndicatorStyle}>
              <InlineIcon icon={<VideoIcon.Filled />} /> {t("media_type_video")}
            </Text>
          ) : null}
        </div>
      </div>
    );
  },
);

GuideInfo.displayName = "GuideInfo";
